import { Injectable, signal, inject } from "@angular/core";
import { forkJoin, concatMap, Observable, of, first } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { GateFault } from "../../../../../models/api/fault/gateFault";
import { TelemetryResponse } from "../../../../../models/shared/telemetry";
import { StatusTableRow } from "../../../../../models/ui/statusTableRow";
import { RealtimeTelemetryResponse } from "../../../../../models/ws/realtimeTelemetry";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { GateStatusService } from "../../../../../services/api/status/status.service";
import {
  SeveritySubscriptionHandle,
  SeverityService,
} from "../../../../../services/websocket/severity/severity.service";
import { RealtimeSeverity } from "../../../../../models/ws/realtimeSeverity";
import { DOCUMENT } from "@angular/common";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { RealtimeGateFaultResponse } from "../../../../../models/ws/realtimeGateFault";
import { GateFaultUtilsService } from "../../../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { SEVERITY } from "../../../../../constants/severities";
import {
  TimeseriesSubscriptionHandle,
  TimeseriesWebsocketService,
} from "../../../../../services/websocket/timeseries-websocket/timeseries-websocket.service";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../../../services/websocket/gate-fault/gate-fault-websocket.service";

const TELEMETRY_KEYS: string[] = [
  "Display_Sensor_Temp",
  "Display_Sensor_Humi",
  "gate_fully_open_movement_time_avg",
  "gate_fully_close_movement_time_avg",
  "gate_full_cycles",
  "cycle",
];
const KEYS_TO_CONVERT: string[] = ["gate_fully_open_movement_time_avg", "gate_fully_close_movement_time_avg"];
@Injectable()
export class TableStatusService {
  public readonly table = signal<StatusTableRow[] | null>(null);

  private gateStatus = inject(GateStatusService);
  private telemetryService = inject(TelemetryService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private assetService = inject(AssetService);
  private severityWebsocket = inject(SeverityService);
  private timeseriesWebsocket = inject(TimeseriesWebsocketService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);

  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;
  private severitySubscription?: SeveritySubscriptionHandle;
  private gateFaultSubscription?: GateFaultSubscriptionHandle;
  private timeseriesSubscription?: TimeseriesSubscriptionHandle;

  constructor() {
    this.assetService
      .getAssets(ASSET_TYPES.CUSTOMER)
      .pipe(first())
      .subscribe({
        next: (customers) => {
          const customerIds = customers.filter((c) => c.id).map((c) => c.id) as string[];
          this.initRealtimeSeverity(customerIds);
        },
      });
  }

  /**
   * Resets the table state and fetches all the necessary data for the table based on provided sourceId and sourceType.
   * @param sourceId the source id of the gate systems to fetch
   * @param sourceType the type of the source e.g. customer, location, etc.
   * @returns a Subscription that can be used to cancel the request
   */
  public selectAssetData(sourceId: string, sourceType: string) {
    this.resetStates();
    const telemetry$ = this.fetchTelemetry(sourceId, sourceType);
    const gateStatus$ = this.fetchGateStatus(sourceId, sourceType);

    forkJoin({
      telemetry: telemetry$,
      gateStatus: gateStatus$,
    }).subscribe({
      next: (res) => {
        const gateStatus = res.gateStatus;
        const telemetry = res.telemetry;
        this.extractAndSetTable(gateStatus, telemetry, this.table.set.bind(this.table));
        const assetIds = gateStatus.map((asset) => asset.id);
        this.initRealtimeTelemetry(assetIds);
        this.initRealtimeGateFaults(assetIds);
      },
    });
  }

  /**
   * Destroys the real-time WebSocket connection.
   * This is used when the user navigates away from the Status Table page.
   */
  public destroy() {
    this.resetStates();
    this.severitySubscription?.close();
    this.gateFaultSubscription?.close();
    this.timeseriesSubscription?.close();
  }

  /**
   * Extracts the necessary telemetry data and gate faults from the provided data
   * and enriches the assets with the extracted data.
   * @param assets the gate systems to enrich with telemetry data and gate faults
   * @param telemetryData the telemetry data to extract from
   * @param tableStateSetter a function that sets the table state
   */
  private extractAndSetTable(
    assets: StatusTableRow[],
    telemetryData: Record<string, TelemetryResponse>,
    tableStateSetter: (value: StatusTableRow[]) => void
  ) {
    assets.forEach((asset) => {
      // Enriches the asset with the telemetry data:
      TELEMETRY_KEYS.forEach((key) => {
        const telemetryValue = telemetryData[asset.id]?.[key]?.[0]?.value || null;
        if (telemetryValue !== null && typeof telemetryValue === "number") {
          asset[key] =
            this.convertToMs(telemetryValue, key)?.toString() !== "NaN" ? this.convertToMs(telemetryValue, key) : "";
        }
      });
      if (asset.cycle !== undefined && asset.gate_full_cycles !== undefined) {
        asset.dirtyUseRatio = this.calcDirtyRatio(asset.cycle, asset.gate_full_cycles);
      }
    });
    tableStateSetter(assets);
  }
  /**
   * Given a list of GateFault objects, extracts the most important one
   * by applying the following rules:
   *
   * Rule 1: CRITICAL faults come before MAJOR faults
   * Rule 2: If severity is the same, 'Completed' repairStatus loses priority
   * Rule 3: If severity and repairStatus are the same, compare by timestamp (greater is better)
   *
   * @param gateFaults the list of GateFault objects to sort and extract
   * @returns the most important GateFxault, or null if the list is empty
   */
  private extractStatusAndRepair(gateFaults: GateFault[]) {
    // Sort objects based on the rules
    const sortedObjects = gateFaults.sort((a, b) => {
      // Rule 1: Compare severity - CRITICAL comes before MAJOR
      if (a.faultType === SEVERITY.CRITICAL.status && b.faultType === SEVERITY.MAJOR.status) return -1;
      if (a.faultType === SEVERITY.MAJOR.status && b.faultType === SEVERITY.CRITICAL.status) return 1;

      // Rule 2: If severity is the same, repairStatus is compared ('Completed' loses priority)
      if (a.faultType === b.faultType) {
        if (a.repairStatus === "Completed" && b.repairStatus !== "Completed") return 1;
        if (b.repairStatus === "Completed" && a.repairStatus !== "Completed") return -1;

        // Rule 3: If severity and repairStatus are the same, compare by timestamp (greater is better)
        if (b.triggeredAt !== undefined && a.triggeredAt !== undefined) return b.triggeredAt - a.triggeredAt;
        else return 0;
      }

      // Default case - No sorting needed
      return 0;
    });

    // Return the first valid object after sorting, or null if no objects are left
    return sortedObjects.length > 0 ? sortedObjects[0] : null;
  }
  /**
   * Converts the given value from milliseconds to seconds for specific keys.
   *
   * @param value - The numeric value to be converted.
   * @param key - The key associated with the value, used to determine if conversion is necessary.
   * @returns The converted value in seconds if the key is in KEYS_TO_CONVERT, otherwise returns the original value.
   */

  private convertToMs(value: number, key: string) {
    if (KEYS_TO_CONVERT.includes(key)) {
      return Math.round((value / 1000) * 10) / 10;
    } else {
      return value;
    }
  }
  /**
   * Calculates the dirty use ratio as a percentage.
   *
   * @param loadChanges - The number of load changes.
   * @param loadCycles - The number of load cycles.
   * @returns The dirty use ratio in percentage, rounded to one decimal place, or undefined if inputs are invalid.
   */

  private calcDirtyRatio(loadChanges: number, loadCycles: number) {
    // Handle edge case: no cycles and no changes
    if (loadCycles === 0 && loadChanges === 0) {
      return 0;
    }

    // Handle edge case: no cycles but changes exist
    if (loadCycles === 0 && loadChanges > 0) {
      return 100;
    }

    // Handle edge case: cycles exist but no changes
    if (loadCycles > 0 && loadChanges === 0) {
      return -100;
    }

    // Normal calculation
    const doubleCycles = loadCycles * 2;
    const diffLoadChangesDoubleCycles = loadChanges - doubleCycles;
    // dirty use ratio in percentage
    let dirtyUseRatio = (diffLoadChangesDoubleCycles / doubleCycles) * 100;
    dirtyUseRatio = Math.round(dirtyUseRatio * 10) / 10;
    return dirtyUseRatio;
  }
  /**
   * Resets the table state signal to null.
   * This is typically used to clear the table data when the component is reset or reinitialized.
   */
  private resetStates() {
    this.table.set(null);
  }

  /**
   * Initializes the real-time WebSocket connection for the status table.
   * @param ids Array of gate system IDs to listen for attributes.
   */
  private initRealtimeTelemetry(ids: string[]) {
    if (ids.length > 0) {
      const descriptor = {
        entityType: ASSET_TYPES.GATE_SYSTEM,
        entityId: ids,
        keys: TELEMETRY_KEYS,
      };
      if (this.timeseriesSubscription) {
        this.timeseriesSubscription.replace(descriptor);
        return;
      }
      const subscription = this.timeseriesWebsocket.register(descriptor);
      this.timeseriesSubscription = subscription;
      subscription.messages$.subscribe({
        next: (incomingData) => {
          this.handleRealtimeTableData(incomingData.data, incomingData.entityId?.toString());
        },
        error: () => undefined,
      });
    }
  }

  private initRealtimeSeverity(customerIds: string[]) {
    this.severitySubscription?.close();
    const subscription = this.severityWebsocket.register({ customerIds });
    this.severitySubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData: RealtimeSeverity) => {
        if (incomingData.assetType === ASSET_TYPES.GATE_SYSTEM) {
          this.table.update((statusTable) => {
            if (!statusTable) return [];
            const matchingRow = statusTable.find((row) => row.id === incomingData.assetId);
            if (matchingRow) {
              matchingRow.severity = incomingData.severity;
            }

            return [...statusTable];
          });
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Initializes the real-time WebSocket connection for gate faults.
   * @param ids The array of gate system IDs to listen for gate faults.
   */
  private initRealtimeGateFaults(ids: string[]) {
    const descriptor = {
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: ids,
      lang: this.localStorage?.getItem("lang"),
    };
    if (this.gateFaultSubscription) {
      this.gateFaultSubscription.replace(descriptor);
      return;
    }
    const subscription = this.gateFaultWebsocket.register(descriptor);
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          this.handleRealtimeGateFault(incomingData);
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Handles an incoming real-time gate fault message and updates the repair status of the corresponding asset in the table.
   * @param incomingGateFaultData The incoming real-time gate fault message.
   */
  private handleRealtimeGateFault(incomingGateFaultData: RealtimeGateFaultResponse) {
    this.table.update((table) => {
      const assetInTable = table?.find((tableAsset) => {
        return tableAsset.id === incomingGateFaultData.entityId;
      });
      if (assetInTable) {
        const consideredFault = this.getRelevantFault(incomingGateFaultData, assetInTable);
        if (consideredFault) {
          assetInTable.repairStatus = consideredFault.repairStatus;
        }
      }
      return table ? [...table] : null;
    });
  }

  /**
   * Returns the most important GateFault for the given asset, taking into account both the current
   * GateFault and the incoming RealtimeGateFaultResponse. If the incoming GateFault is not active,
   * returns null.
   *
   * @param res The incoming RealtimeGateFaultResponse.
   * @param asset The asset for which to retrieve the most important GateFault.
   * @returns The most important GateFault, or null if the incoming GateFault is not active.
   */
  private getRelevantFault(res: RealtimeGateFaultResponse, asset: StatusTableRow) {
    if (asset.id === res.entityId && res.data?.active) {
      const currentFault: GateFault = {
        triggeredAt: 0,
        faultType: asset?.severity,
        repairStatus: asset.repairStatus,
      };
      const incomingGateFault = this.gateFaultUtils.convertGateFault(res.data);
      const consideredFault = this.extractStatusAndRepair([incomingGateFault, currentFault]);
      if (consideredFault) {
        return consideredFault;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Updates the status table with the latest telemetry data for a given gate system.
   * @param incomingDataPoint - Latest telemetry data for a gate system.
   * @param gateSystemId - ID of the gate system.
   */
  private handleRealtimeTableData(incomingDataPoint: RealtimeTelemetryResponse, gateSystemId: string) {
    // Extract and convert all present data points
    const updates: Partial<Record<keyof RealtimeTelemetryResponse, number>> = {};

    for (const key in incomingDataPoint) {
      const entry = incomingDataPoint[key];
      if (entry && entry.value !== undefined) {
        const value = this.convertToMs(entry.value, key);
        updates[key] = value;
      }
    }

    // If no valid data points found, return early
    if (Object.keys(updates).length === 0) {
      return;
    }

    // Update the table with all collected data points
    this.table.update((statusTable) => {
      if (!statusTable) return [];
      const table: StatusTableRow[] = [...statusTable];
      const foundIndex = table.findIndex((asset) => asset.id === gateSystemId);

      if (foundIndex > -1) {
        // Apply all updates at once
        Object.entries(updates).forEach(([key, value]) => {
          table[foundIndex][key] = value;
        });
      }

      return table;
    });
  }

  /**
   * Fetches the status of gate systems associated with a specific source.
   * Utilizes shared replay to cache the most recent result.
   *
   * @param sourceId - The ID of the source (e.g., asset or location).
   * @param sourceType - The type of the source (e.g., asset or location).
   * @returns An observable that emits an array of status table rows.
   */

  private fetchGateStatus(sourceId: string, sourceType: string) {
    return this.gateStatus.getAssetStatus(sourceId, sourceType);
  }

  /**
   * Fetches telemetry data for gate systems associated with a given source.
   * @param sourceId - ID of the source (asset or location).
   * @param sourceType - Type of the source (asset or location).
   * @returns An observable emitting a map of asset IDs to their telemetry data.
   */
  private fetchTelemetry(sourceId: string, sourceType: string) {
    return this.fetchGateStatus(sourceId, sourceType).pipe(
      concatMap((assets) => {
        const assetTelemetry$: Record<string, Observable<TelemetryResponse>> = {};
        assets.forEach((asset) => {
          if (asset.id) {
            assetTelemetry$[asset.id] = this.telemetryService.getTimeseries(
              asset.id,
              ASSET_TYPES.GATE_SYSTEM,
              TELEMETRY_KEYS.join(",")
            );
          }
        });

        if (Object.keys(assetTelemetry$).length === 0) {
          return of({});
        }

        return forkJoin(assetTelemetry$);
      })
    );
  }
}
